/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import Select from "react-select";
import GenericModal from "components/Modal";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { Domain, OptionProps } from "../sharedUtils";
import { RefreshCw, X } from "lucide-react";
import { customSelectStyles } from "util/selectStyles";

// --- UPLOAD TRAINING MODAL ---
export function UploadTrainingModal({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
    const { addToast } = useToaster();
    const [domains, setDomains] = useState<Domain[]>([]);
    const [trainingName, setTrainingName] = useState("");
    const [selectedDomainId, setSelectedDomainId] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Fetch domains when the modal opens
            apiService.get("api/gp/domains")
                .then(res => setDomains(res.data || []))
                .catch(() => { });
        } else {
            // Clear the form state when the modal closes
            setTrainingName("");
            setSelectedDomainId("");
            setSelectedFile(null);
            setUploading(false);
        }
    }, [isOpen]);

    const getStorageType = async () => {
        try {
            const storageRes = await apiService.get("api/admin/storage");
            return storageRes?.data?.storage || "DRIVE";
        } catch (error) {
            console.log("Failed to detect storage type, using DRIVE flow", error);
            return "DRIVE";
        }
    };

    const uploadFileToS3 = async (formData: FormData, file: File) => {
        const uploadUrlRes = await apiService.post("api/upload-url", {
            fileName: file.name,
        });

        const { uploadUrl, key } = uploadUrlRes?.data || {};

        if (!uploadUrl || !key) {
            throw new Error("Missing uploadUrl or key from upload-url response");
        }

        const putResponse = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
        });

        if (!putResponse.ok) {
            throw new Error("File upload to storage failed");
        }

        // Appending 'key' for single file 
        // Note: change to "keys" if your backend DTO expects an array like the AddProduct component
        formData.append("key", key);
    };

    const handleFileUploadByStorageType = async (
        formData: FormData,
        storageType: string,
        file: File
    ) => {
        if (storageType === "S3") {
            await uploadFileToS3(formData, file);
            return;
        }

        // Drive flow (Default)
        formData.append("file", file);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB
            if (file.size > maxSizeInBytes) {
                addToast("File size must be less than 5 MB.", "error");
                e.target.value = ""; // Reset input
                setSelectedFile(null);
                return;
            }
            setSelectedFile(file);
        } else {
            setSelectedFile(null);
        }
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile || !trainingName.trim() || !selectedDomainId) return;
        try {
            setUploading(true);
            const formData = new FormData();

            formData.append("name", trainingName.trim());
            formData.append("domainId", selectedDomainId);

            const storageType = await getStorageType();

            await handleFileUploadByStorageType(formData, storageType, selectedFile);

            const res = await apiService.post("api/gp/trainings/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            addToast(res.data?.message || "Document uploaded. AI processing started.", "success");
            onSuccess();
            onClose();
        } catch (error: any) {
            const errorMessage = error?.response?.data?.error || error?.message || "Failed to upload document.";
            addToast(errorMessage, "error");
        } finally {
            setUploading(false);
        }
    };

    if (!isOpen) return null;
    return (
        <GenericModal isOpen={isOpen} onClose={onClose} header="Upload Training Document" customWidth="500px">
            <form onSubmit={handleUpload} className="p-6 space-y-5 w-full overflow-x-hidden">
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Training Name <span className="text-red-500">*</span></label>
                    <input type="text" value={trainingName} onChange={(e) => setTrainingName(e.target.value)} placeholder="e.g. React Fundamentals" className="w-full p-2.5 rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20" required />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Domain <span className="text-red-500">*</span></label>
                    <select value={selectedDomainId} onChange={(e) => setSelectedDomainId(e.target.value)} className="w-full p-2.5 rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20" required>
                        <option value="">-- Select a Domain --</option>
                        {domains.map(d => <option key={d.id} value={d.id}>{d.domain}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Document (PDF/DOCX) <span className="text-red-500">*</span></label>
                    <input
                        type="file"
                        accept=".pdf,.docx"
                        onChange={handleFileChange}
                        className="w-full p-2 text-sm border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-gray-200 cursor-pointer"
                        required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Size below 5 MB is allowed.</p>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">Cancel</button>
                    <button type="submit" disabled={uploading || !trainingName || !selectedFile || !selectedDomainId} className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                        {uploading ? "Uploading..." : 'Upload & Process'}
                    </button>
                </div>
            </form>
        </GenericModal>
    );
}

// --- ASSIGN TRAINING MODAL ---
export function AssignTrainingModal({ isOpen, trainingId, onClose }: { isOpen: boolean; trainingId: number | null; onClose: () => void }) {
    const { addToast } = useToaster();

    // Available Users Search State
    const [options, setOptions] = useState<OptionProps[]>([]);
    const [selectedUser, setSelectedUser] = useState<OptionProps | null>(null);
    const [userSearch, setUserSearch] = useState("");
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [hasMoreUsers, setHasMoreUsers] = useState(true);
    const [userPage, setUserPage] = useState(0);
    const USER_PAGE_SIZE = 10;

    // Already Assigned Users State
    const [assignedUsers, setAssignedUsers] = useState<any[]>([]);
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const [selectedUserToRemove, setSelectedUserToRemove] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const [removing, setRemoving] = useState(false);

    const fetchAssignedUsers = async () => {
        if (!trainingId) return;
        setLoadingAssigned(true);
        try {
            const res = await apiService.get(`api/gp/assignments/training/${trainingId}`);
            setAssignedUsers(res.data || []);
        } catch (e) {
            addToast("Failed to fetch assigned users.", "error");
        } finally {
            setLoadingAssigned(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setSelectedUser(null);
            setUserSearch("");
            setUserPage(0);
            fetchUsers(0, "", false);
            fetchAssignedUsers();
        }
    }, [isOpen, trainingId]);

    useEffect(() => {
        if (!isOpen) return;
        const timer = window.setTimeout(() => fetchUsers(0, userSearch, false), 400);
        return () => window.clearTimeout(timer);
    }, [userSearch, isOpen]);

    const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
        setLoadingSearch(true);
        try {
            const query = new URLSearchParams({ page: String(page), size: String(USER_PAGE_SIZE) });
            if (searchValue.trim()) query.set("search", searchValue.trim());

            const res = await apiService.get(`api/users?${query.toString()}`);
            const payload = Array.isArray(res.data) ? res.data : (res.data?.content || []);
            const fetchedOptions = payload.map((u: any) => ({
                value: u.id || u.email,
                label: u.displayName || u.name || u.email
            }));

            setOptions((prev) => {
                if (!append || page === 0) return fetchedOptions.sort((a: any, b: any) => a.label.localeCompare(b.label));
                const merged = [...prev, ...fetchedOptions];
                const dedup = new Map<string, OptionProps>();
                merged.forEach((item) => { if (!dedup.has(item.value)) dedup.set(item.value, item); });
                return Array.from(dedup.values()).sort((a, b) => a.label.localeCompare(b.label));
            });

            setUserPage(page);
            setHasMoreUsers(fetchedOptions.length === USER_PAGE_SIZE);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingSearch(false);
        }
    };

    const handleAssign = async () => {
        if (!trainingId || !selectedUser) return;
        try {
            await apiService.post(`api/gp/assignments/${selectedUser.value}/${trainingId}`, {});
            addToast("User assigned successfully!", "success");
            setSelectedUser(null);
            fetchAssignedUsers();
        } catch (error: any) {
            addToast(error?.response?.data?.error || "Failed to assign user.", "error");
        }
    };

    const handleRemoveClick = (userId: number, userName: string) => {
        setSelectedUserToRemove({
            id: userId,
            name: userName,
        });
        setOpenRemoveModal(true);
    };

    const handleConfirmRemove = async () => {
        if (!trainingId || !selectedUserToRemove || removing) return;

        setRemoving(true);

        try {
            await apiService.delete(
                `api/gp/assignments/${selectedUserToRemove.id}/${trainingId}`
            );

            addToast("User unassigned successfully.", "success");

            setOpenRemoveModal(false);
            setSelectedUserToRemove(null);

            fetchAssignedUsers();
        } catch (error: any) {
            addToast(
                error?.response?.data?.error || "Failed to unassign user.",
                "error"
            );
        } finally {
            setRemoving(false);
        }
    };

    const handleInputChange = (val: string, actionMeta: any) => {
        if (actionMeta.action === "input-change") setUserSearch(val);
        return val;
    };

    const handleChange = (selected: any, actionMeta: any) => {
        setSelectedUser(selected);
        if (actionMeta.action === "clear") {
            setUserSearch("");
            fetchUsers(0, "", false);
        }
    };

    if (!isOpen) return null;
    return (
        <GenericModal disableContentScroll isOpen={isOpen} onClose={onClose} header="Manage Training Assignments" customWidth="600px">
            <div className="p-2 space-y-2 max-h-[80vh] w-full ">

                {/* TOP SECTION: Add New Assignment */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                    <label className="block text-sm font-semibold mb-2 text-gray-800 dark:text-gray-200">Assign New User</label>
                    <div className="flex items-start gap-3">
                        <div className="flex-1">
                            <Select
                                options={options}
                                placeholder="Search to assign..."
                                className="w-full text-slate-900"
                                styles={customSelectStyles}
                                isLoading={loadingSearch}
                                isClearable
                                menuPosition="fixed"
                                onInputChange={handleInputChange}
                                onChange={handleChange}
                                value={selectedUser}
                                onMenuScrollToBottom={() => { if (hasMoreUsers && !loadingSearch) fetchUsers(userPage + 1, userSearch, true); }}
                            />
                        </div>
                        <button
                            onClick={handleAssign}
                            disabled={!selectedUser}
                            className="px-5 py-2 h-[38px] text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                            Assign
                        </button>
                    </div>
                </div>

                {/* BOTTOM SECTION: Existing Assignments */}
                <div className="flex flex-col">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
                        Currently Assigned Users
                    </h4>

                    <div className="max-h-[320px] overflow-y-auto pr-2">
                        {loadingAssigned ? (
                            <div className="flex justify-center py-6">
                                <RefreshCw className="animate-spin text-gray-400" size={20} />
                            </div>
                        ) : assignedUsers.length === 0 ? (
                            <div className="text-center py-6 text-sm text-gray-500">
                                No users currently assigned.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {assignedUsers.map(user => (
                                    <div
                                        key={user.id}
                                        className="flex justify-between items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
                                    >
                                        <div>
                                            <p className="font-medium text-sm text-gray-900 dark:text-white">
                                                {user.userName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {user.userEmail}
                                            </p>
                                        </div>

                                        <button
                                            onClick={() => handleRemoveClick(user.userId, user.userName)}
                                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded transition-colors"
                                        >
                                            <X size={14} />
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <GenericModal
                    isOpen={openRemoveModal}
                    onClose={() => {
                        setOpenRemoveModal(false);
                        setSelectedUserToRemove(null);
                    }}
                    onAccept={handleConfirmRemove}
                    onDecline={() => {
                        setOpenRemoveModal(false);
                        setSelectedUserToRemove(null);
                    }}
                    acceptText={removing ? "Removing..." : "Remove"}
                    cancelText="Cancel"
                    header="Remove Assigned User"
                    customWidth="500px"
                >
                    <p className="text-base text-slate-600 dark:text-slate-300 mt-2">
                        Are you sure you want to remove{" "}
                        <span className="font-semibold text-slate-900 dark:text-white">
                            {selectedUserToRemove?.name}
                        </span>{" "}
                        from this training?
                    </p>
                </GenericModal>
                {/* Modal Footer Space Buffer */}

                <div className="flex justify-end pt-1 mt-1 border-t border-gray-100 dark:border-gray-800">
                    <button onClick={onClose} className="px-6 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 transition-colors">Close</button>
                </div>
            </div>
        </GenericModal>
    );
}